import * as Cesium from "cesium";
import { useEffect, useRef } from "react";

import { animateCameraPivotInPlaceForMeasurement } from "./measurementCamera";
import {
  addMeasurementEntities,
  removeMeasurementEntities,
  removePreviewEntities,
  syncPendingPointEntity,
  updateMeasurementEntities,
  upsertPreviewEntities,
} from "./measurementEntities";
import {
  computeHeightDistanceFromCursor,
  findGroundPointAtScreenPosition,
  projectToWindow,
} from "./measurementGeometry";
import {
  findDraggableMeasurementHandle,
  setCameraInputsEnabled,
  type DraggingMeasurementState,
} from "./measurementInteraction";
import {
  deserializePoint,
  formatDistanceLabel,
  useMeasurementStore,
} from "@/stores/measurementStore";

interface MeasurementPluginProps {
  viewer: Cesium.Viewer | null;
}

export const MeasurementPlugin = ({ viewer }: MeasurementPluginProps) => {
  const measurements = useMeasurementStore((state) => state.measurements);
  const pendingFirstPoint = useMeasurementStore(
    (state) => state.pendingFirstPoint,
  );
  const isMeasurementModeActive = useMeasurementStore(
    (state) => state.isMeasurementModeActive,
  );
  const measurementType = useMeasurementStore((state) => state.measurementType);
  const setPendingFirstPoint = useMeasurementStore(
    (state) => state.setPendingFirstPoint,
  );
  const addMeasurement = useMeasurementStore((state) => state.addMeasurement);
  const updateMeasurement = useMeasurementStore(
    (state) => state.updateMeasurement,
  );
  const stopMeasurementSession = useMeasurementStore(
    (state) => state.stopMeasurementSession,
  );

  const heightInitialScreenYRef = useRef<number | null>(null);
  const wasMeasurementModeActiveRef = useRef(false);
  const cameraPivotAnimationFrameRef = useRef<number | null>(null);
  const draggingMeasurementRef = useRef<DraggingMeasurementState | null>(null);

  useEffect(() => {
    const wasActive = wasMeasurementModeActiveRef.current;
    wasMeasurementModeActiveRef.current = isMeasurementModeActive;

    if (!viewer || viewer.isDestroyed()) return;
    if (!isMeasurementModeActive || wasActive) return;

    animateCameraPivotInPlaceForMeasurement({
      viewer,
      measurementType,
      animationFrameRef: cameraPivotAnimationFrameRef,
    });
  }, [isMeasurementModeActive, measurementType, viewer]);

  useEffect(
    () => () => {
      if (cameraPivotAnimationFrameRef.current !== null) {
        cancelAnimationFrame(cameraPivotAnimationFrameRef.current);
        cameraPivotAnimationFrameRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    removeMeasurementEntities(viewer);
    measurements.forEach((measurement) =>
      addMeasurementEntities(viewer, measurement),
    );

    syncPendingPointEntity(viewer, pendingFirstPoint);

    if (!pendingFirstPoint) {
      heightInitialScreenYRef.current = null;
      removePreviewEntities(viewer);
    }

    viewer.scene.requestRender();
  }, [measurements, pendingFirstPoint, viewer]);

  useEffect(() => {
    if (!isMeasurementModeActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      stopMeasurementSession();
      setPendingFirstPoint(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMeasurementModeActive, setPendingFirstPoint, stopMeasurementSession]);

  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    handler.setInputAction((movement: { position?: Cesium.Cartesian2 }) => {
      if (!movement.position) return;

      const draggableHandle = findDraggableMeasurementHandle(
        viewer,
        movement.position,
      );

      if (draggableHandle) {
        const measurement = useMeasurementStore
          .getState()
          .measurements.find((item) => item.id === draggableHandle.measurementId);

        if (!measurement) return;

        const startPoint = deserializePoint(measurement.startPoint);
        const endPoint = deserializePoint(measurement.endPoint);
        const draggedPointRole = draggableHandle.pointRole;
        const fixedPoint =
          draggedPointRole === "start"
            ? Cesium.Cartesian3.clone(endPoint)
            : Cesium.Cartesian3.clone(startPoint);

        draggingMeasurementRef.current = {
          measurementId: measurement.id,
          measurementType: measurement.type,
          draggedPointRole,
          fixedPoint,
          currentStartPoint: Cesium.Cartesian3.clone(startPoint),
          currentEndPoint: Cesium.Cartesian3.clone(endPoint),
          heightReferenceScreenY:
            measurement.type === "height-from-ground"
              ? projectToWindow(viewer, fixedPoint)?.y ?? movement.position.y
              : null,
        };

        setCameraInputsEnabled(viewer, false);
        removePreviewEntities(viewer);
        return;
      }

      if (!isMeasurementModeActive) return;

      const storeState = useMeasurementStore.getState();
      const currentFirstPoint = storeState.pendingFirstPoint
        ? deserializePoint(storeState.pendingFirstPoint)
        : null;

      if (!currentFirstPoint) {
        const firstPoint = findGroundPointAtScreenPosition(
          viewer,
          movement.position,
        );
        if (!firstPoint) return;

        if (measurementType === "height-from-ground") {
          heightInitialScreenYRef.current = movement.position.y;
        } else {
          heightInitialScreenYRef.current = null;
        }
        setPendingFirstPoint(firstPoint);
        return;
      }

      const measurementPreview: {
        endPoint: Cesium.Cartesian3;
        distanceMeters: number;
      } | null =
        measurementType === "height-from-ground"
          ? (() => {
              const initialScreenY = heightInitialScreenYRef.current;
              if (initialScreenY === null) return null;

              return computeHeightDistanceFromCursor({
                viewer,
                startPoint: currentFirstPoint,
                cursorScreenY: movement.position.y,
                fallbackStartScreenY: initialScreenY,
              });
            })()
          : (() => {
              const secondPoint = findGroundPointAtScreenPosition(
                viewer,
                movement.position,
              );
              if (!secondPoint) {
                return null;
              }
              return {
                endPoint: secondPoint,
                distanceMeters: Cesium.Cartesian3.distance(
                  currentFirstPoint,
                  secondPoint,
                ),
              };
            })();

      if (!measurementPreview) return;
      const { endPoint, distanceMeters } = measurementPreview;

      addMeasurement({
        type: measurementType,
        startPoint: currentFirstPoint,
        endPoint,
        distanceMeters,
      });

      // Session one-shot: après la 2e sélection, on sort du mode mesure.
      stopMeasurementSession();
      heightInitialScreenYRef.current = null;
      setPendingFirstPoint(null);
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    handler.setInputAction((movement: { endPosition?: Cesium.Cartesian2 }) => {
      if (!movement.endPosition) return;

      const draggingMeasurement = draggingMeasurementRef.current;
      if (draggingMeasurement) {
        const draggedMeasurementPreview: {
          startPoint: Cesium.Cartesian3;
          endPoint: Cesium.Cartesian3;
          distanceMeters: number;
        } | null =
          draggingMeasurement.measurementType === "height-from-ground"
            ? (() => {
                const heightReferenceScreenY =
                  draggingMeasurement.heightReferenceScreenY;
                if (heightReferenceScreenY === null) return null;

                const heightPreview = computeHeightDistanceFromCursor({
                  viewer,
                  startPoint: draggingMeasurement.fixedPoint,
                  cursorScreenY: movement.endPosition.y,
                  fallbackStartScreenY: heightReferenceScreenY,
                });

                return draggingMeasurement.draggedPointRole === "start"
                  ? {
                      startPoint: heightPreview.endPoint,
                      endPoint: draggingMeasurement.fixedPoint,
                      distanceMeters: heightPreview.distanceMeters,
                    }
                  : {
                      startPoint: draggingMeasurement.fixedPoint,
                      endPoint: heightPreview.endPoint,
                      distanceMeters: heightPreview.distanceMeters,
                    };
              })()
            : (() => {
                const draggedPoint = findGroundPointAtScreenPosition(
                  viewer,
                  movement.endPosition,
                );
                if (!draggedPoint) {
                  return null;
                }

                return draggingMeasurement.draggedPointRole === "start"
                  ? {
                      startPoint: draggedPoint,
                      endPoint: draggingMeasurement.fixedPoint,
                      distanceMeters: Cesium.Cartesian3.distance(
                        draggedPoint,
                        draggingMeasurement.fixedPoint,
                      ),
                    }
                  : {
                      startPoint: draggingMeasurement.fixedPoint,
                      endPoint: draggedPoint,
                      distanceMeters: Cesium.Cartesian3.distance(
                        draggingMeasurement.fixedPoint,
                        draggedPoint,
                      ),
                    };
              })();

        if (!draggedMeasurementPreview) return;

        draggingMeasurement.currentStartPoint = draggedMeasurementPreview.startPoint;
        draggingMeasurement.currentEndPoint = draggedMeasurementPreview.endPoint;

        updateMeasurement({
          id: draggingMeasurement.measurementId,
          startPoint: draggedMeasurementPreview.startPoint,
          endPoint: draggedMeasurementPreview.endPoint,
          distanceMeters: draggedMeasurementPreview.distanceMeters,
        });
        updateMeasurementEntities(viewer, {
          measurementId: draggingMeasurement.measurementId,
          measurementType: draggingMeasurement.measurementType,
          startPoint: draggedMeasurementPreview.startPoint,
          endPoint: draggedMeasurementPreview.endPoint,
          distanceLabel: formatDistanceLabel(
            draggedMeasurementPreview.distanceMeters,
          ),
        });
        viewer.scene.requestRender();
        return;
      }

      if (!isMeasurementModeActive) return;
      if (!pendingFirstPoint) {
        removePreviewEntities(viewer);
        return;
      }

      const startPoint = deserializePoint(pendingFirstPoint);
      const previewMeasurement: {
        endPoint: Cesium.Cartesian3;
        distanceMeters: number;
      } | null =
        measurementType === "height-from-ground"
          ? (() => {
              const initialScreenY = heightInitialScreenYRef.current;
              if (initialScreenY === null) return null;

              return computeHeightDistanceFromCursor({
                viewer,
                startPoint,
                cursorScreenY: movement.endPosition.y,
                fallbackStartScreenY: initialScreenY,
              });
            })()
          : (() => {
              const hoveredPoint = findGroundPointAtScreenPosition(
                viewer,
                movement.endPosition,
              );
              if (!hoveredPoint) {
                return null;
              }
              return {
                endPoint: hoveredPoint,
                distanceMeters: Cesium.Cartesian3.distance(
                  startPoint,
                  hoveredPoint,
                ),
              };
            })();

      if (!previewMeasurement) {
        removePreviewEntities(viewer);
        viewer.scene.requestRender();
        return;
      }

      const { endPoint, distanceMeters } = previewMeasurement;

      upsertPreviewEntities(viewer, {
        startPoint,
        endPoint,
        label: formatDistanceLabel(distanceMeters),
        clampToGround: measurementType === "distance-ground",
      });
      viewer.scene.requestRender();
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(() => {
      if (draggingMeasurementRef.current) {
        setCameraInputsEnabled(viewer, true);
      }
      draggingMeasurementRef.current = null;
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    return () => {
      if (draggingMeasurementRef.current) {
        setCameraInputsEnabled(viewer, true);
      }
      draggingMeasurementRef.current = null;
      removePreviewEntities(viewer);
      handler.destroy();
    };
  }, [
    addMeasurement,
    isMeasurementModeActive,
    pendingFirstPoint,
    measurementType,
    stopMeasurementSession,
    setPendingFirstPoint,
    updateMeasurement,
    viewer,
  ]);

  // Si le mode est désactivé, on supprime l'éventuel point initial en attente
  // pour éviter un état ambigu au prochain démarrage.
  useEffect(() => {
    if (isMeasurementModeActive) return;
    heightInitialScreenYRef.current = null;
    setPendingFirstPoint(null);
  }, [isMeasurementModeActive, setPendingFirstPoint]);

  return null;
};
